import { Button } from '@spark-web/button';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { parseAsString, useQueryStates } from 'nuqs';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useOutletContext } from 'react-router';

import { usePartnerConfig } from '~/context/PartnerConfigContext';
import { CustomerFormValues, OutletContextType } from '~/types/marketplace';

import { Alert } from '../common/alert';

import { CustomerFormField } from './components/customer-form-field';

export type RequestQuoteFormProps = {
  onSubmit: (data: Partial<CustomerFormValues>) => void;
  isLoading: boolean;
  error: Record<string, string> | string | undefined;
  leadType?: 'Marketplace' | 'Concierge';
  excludedFields?: (keyof CustomerFormValues)[];
  isAddVendorFlow?: boolean;
  heading?: string;
};

export const RequestQuoteForm = ({
  onSubmit,
  isLoading: isSubmitting,
  error,
  leadType = 'Concierge',
  excludedFields,
  isAddVendorFlow = false,
  heading,
}: RequestQuoteFormProps) => {
  const { partner, channel: channelFromContext } = useOutletContext<OutletContextType>();

  const config = usePartnerConfig();
  const activeChannel = config?.configuration?.channels?.find((c) => c.slug === channelFromContext);
  const channelFinancialProductId = activeChannel?.financialProductId;

  const formProvider = useFormContext();

  const [isValidating, setIsValidating] = useState(false);
  const isLoading = isSubmitting || isValidating;

  const onCustomerFormFieldValidating = (isValidating: boolean) => {
    setIsValidating(isValidating);
  };

  const [{ source, sourceReference, intent, interestedInFinance }] = useQueryStates({
    source: parseAsString.withDefault(''),
    sourceReference: parseAsString.withDefault(''),
    intent: parseAsString.withDefault(''),
    interestedInFinance: parseAsString.withDefault(''),
  });

  const {
    handleSubmit,
    register,
    formState: { isValid },
  } = formProvider;

  return (
    <Stack gap="xlarge">
      {heading && (
        <Stack paddingBottom="large">
          <Text size="large" weight="semibold">
            {heading}
          </Text>
        </Stack>
      )}
      <CustomerFormField
        leadType={leadType}
        excludedFields={excludedFields}
        onPartnerCustomerIdValidating={onCustomerFormFieldValidating}
      />
      <input type="hidden" value={partner} {...register('partner')} />
      {channelFromContext && <input type="hidden" value={channelFromContext} {...register('channel')} />}
      {channelFinancialProductId && (
        <input type="hidden" value={channelFinancialProductId} {...register('financialProductId')} />
      )}
      <input type="hidden" value={source || ''} {...register('source')} />
      <input type="hidden" value={sourceReference || ''} {...register('sourceReference')} />
      <input type="hidden" value={intent || ''} {...register('intent')} />
      <input type="hidden" value={interestedInFinance || ''} {...register('interestedInFinance')} />
      {error && !isLoading ? <Alert error="REQUEST_ERROR" /> : null}
      <Stack gap="xxlarge" marginTop="medium">
        {isAddVendorFlow ? (
          <>
            <Text size="small" weight="semibold">
              You can add more vendors in the next step. Your details will not be shared until you confirm your vendor
              selection.
            </Text>
            <Button disabled={!isValid} loading={isLoading} onClick={handleSubmit(onSubmit)}>
              Continue
            </Button>
          </>
        ) : (
          <Button disabled={!isValid} loading={isLoading} onClick={handleSubmit(onSubmit)}>
            Send quote requests
          </Button>
        )}
      </Stack>
    </Stack>
  );
};
